/**
 * Who puts the company stamp on a signature section.
 *
 * A signature section (a clause with isSignature) lists its signatories; at
 * most one of them stamps it. `needStamp` on the clause shows the stamp box in
 * the document, so it follows whether anyone stamps.
 */

/** Where a signer in the send-for-signing list came from in the document. */
export type StampOrigin = { sectionId: string; clauseIndex: number; signerIndex: number };

type StampableClause = {
    isSignature?: boolean;
    needStamp?: boolean;
    signatories?: { fields: { stamp?: boolean } & Record<string, unknown> }[];
};

type StampableSection = { id: string; clauses: StampableClause[] };

/** Sets or clears one signer as the stamper of their section; only one signer stamps a section. */
export function applyClauseStamp<S extends StampableSection>(sections: S[], origin: StampOrigin, on: boolean): S[] {
    return sections.map(section => {
        if (section.id !== origin.sectionId) return section;
        return {
            ...section,
            clauses: section.clauses.map((clause, clauseIndex) => {
                if (clauseIndex !== origin.clauseIndex || !clause.signatories) return clause;
                const signatories = clause.signatories.map((signatory, signerIndex) => ({
                    ...signatory,
                    fields: {
                        ...signatory.fields,
                        stamp: on ? signerIndex === origin.signerIndex : signerIndex === origin.signerIndex ? false : !!signatory.fields.stamp,
                    },
                }));
                return { ...clause, signatories, needStamp: signatories.some(s => !!s.fields.stamp) };
            }),
        };
    });
}
